/**
 * 媒体代理工具
 * 统一使用后端存储代理
 */

/**
 * 为 asset 获取流式代理 URL。
 * 前端统一走同源 /api 路径，避免 next/image 将本地开发环境识别为未配置的远程 host。
 */
export function getAssetStreamUrl(assetId: string): string {
  return `/api/assets/stream/${assetId}`;
}

/**
 * 为 asset 获取封面图 URL。视频封面由后端懒生成并重定向到持久化图片。
 */
export function getAssetThumbnailUrl(assetId: string): string {
  return `/api/assets/${assetId}/thumbnail`;
}

/**
 * 获取视频播放 URL
 * @deprecated 推荐使用 getAssetStreamUrl
 */
export function getAssetProxyUrl(assetId: string): string {
  return `/api/assets/proxy/${assetId}`;
}

// ============================================
// HLS 流式播放 API（保留兼容，实际返回 MP4 流式代理）
// ============================================

/**
 * 获取 HLS 播放列表 URL
 * @param assetId - 资源 ID
 */
export function getHlsPlaylistUrl(assetId: string): string {
  return `/api/assets/hls/${assetId}/playlist.m3u8`;
}

/**
 * 获取 HLS 播放 URL（别名，用于视频资源层分流）
 * @param assetId - 资源 ID
 */
export const getAssetHlsUrl = getHlsPlaylistUrl;

/**
 * 获取 HLS 状态检查 URL
 * @param assetId - 资源 ID  
 */
function getHlsStatusUrl(assetId: string): string {
  return `/api/assets/hls/${assetId}/status`;
}

// 调试开关
const DEBUG_ENABLED = false;
const debugLog = (...args: unknown[]) => { if (DEBUG_ENABLED) console.log('[MediaProxy]', ...args); };

/**
 * 检查资源是否有可用的播放 URL
 * @param assetId - 资源 ID
 * @returns Promise<HlsStatus>
 */
interface HlsStatus {
  available: boolean;
  playlistUrl: string | null;
  assetStatus: string | null;
  hlsStatus: string | null;
  isCloudflare: boolean;
  cloudflareUid: string | null;
  cloudflareStatus: string | null;
}

export async function checkHlsAvailable(assetId: string): Promise<HlsStatus> {
  const url = getHlsStatusUrl(assetId);
  debugLog('🔍 检查播放可用性:', url);
  
  try {
    const response = await fetch(url);
    debugLog('📡 状态响应:', response.status, response.ok);
    
    if (!response.ok) {
      debugLog('⚠️ 状态非 OK:', response.status);
      return { 
        available: false, 
        playlistUrl: null, 
        assetStatus: null,
        hlsStatus: null,
        isCloudflare: false,
        cloudflareUid: null,
        cloudflareStatus: null,
      };
    }
    const data = await response.json();
    debugLog('✅ 状态:', data);
    
    return {
      available: data.available ?? false,
      playlistUrl: data.playlist_url ?? null,
      assetStatus: data.asset_status ?? null,
      hlsStatus: data.hls_status ?? null,
      isCloudflare: false,
      cloudflareUid: null,
      cloudflareStatus: null,
    };
  } catch (error) {
    debugLog('❌ 检查失败:', error);
    return { 
      available: false, 
      playlistUrl: null, 
      assetStatus: null,
      hlsStatus: null,
      isCloudflare: false,
      cloudflareUid: null,
      cloudflareStatus: null,
    };
  }
}
